import React, {PureComponent} from 'react';
import styled from 'styled-components';
import Sizes from '../Assets/Sizes';
import {CSSTransition} from "react-transition-group";
import {FormattedMessage, injectIntl} from "react-intl";
import H2 from "./H2";
import {humanizeDayOfWeek} from "../Common/Helpers";
import {Button} from 'rmwc/Button';
import Confirm from "./Confirm";
import Toast from "./Toast";
import Link from "./Link";
import {lighten} from "../Common/Colors";
import {ModalPrimary, PlainContainer, VerticalSpace} from "../Containers/Identify/CommonUI";
import {isNonEmptyArray} from "@tomeravni/easybizy-js-common/common";
import {extractError} from "@tomeravni/easybizy-js-common/errors";
import {LoaderWrapper} from "./Layout";
import {LinearProgress} from "rmwc/LinearProgress/index";


const EXTRA_BOTTOM_SPACE_FOR_ALLOW_ADDITIONAL = '80px';

const Wrapper = styled.div.attrs({
    className: 'existing-meetings-dialog__wrapper',
})`
    .mask-enter {
        opacity: 0;
    }

    .mask-enter-active {
        opacity: 1;
        transition: all 500ms;
    }

    .mask-exit {
        opacity: 1;
    }

    .mask-exit-active {
        opacity: 0;
        transition: all 500ms;
    }

    .alert-enter {
        transform: translateY(-100vh);
    }

    .alert-enter-active {
        transform: translateY(0);
        transition: transform .5s ease-in;
    }

    .alert-exit {
        opacity: 1;
    }

    .alert-exit-active {
        transform: translateY(-100vh);
        transition: opacity 0.5s, transform 0.5s;
    }

    * {
        font-family: 'Heebo', 'Arimo', 'SourceSansPro', 'LiberationSans', "Helvetica Neue", Helvetica, Arial, sans-serif;
    }

`;

const Mask = styled.div`
    position: fixed;
    background-color: ${p => p.theme.colors.mask};
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 3;
`;

const Separator = styled.div`
    margin-top: ${Sizes.mediumPadding};
    margin-bottom: ${Sizes.footerPoweredByMinHeight};
    height: 1px;
    width: 100%;
    background-color: ${p => lighten(p.theme.colors.primaryTextInverted, 30)};
`;

const Message = styled.div.attrs({
    className: 'existing-meetings-dialog__message',
})`
    position: fixed;
    width: 100%;
    background-color: ${p => p.theme.colors.modalBackground};
    padding-bottom: ${(props) => props.extraBottomPadding ? EXTRA_BOTTOM_SPACE_FOR_ALLOW_ADDITIONAL : Sizes.defaultPadding};
    top: 0;
    left: 0;
    z-index: 4;
    direction: ${p => p.ltr ? 'ltr' : 'rtl'};
`;

const DialogActions = styled.div.attrs({
    className: 'existing-meetings-dialog__actions',
})`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-top: ${Sizes.defaultPadding};
    padding-bottom: ${Sizes.defaultPadding};

    button:not(:last-of-type) {
        margin-left: ${Sizes.smallMargin};
        margin-right: ${Sizes.smallMargin};
    }

    button:not(:first-of-type) {
        margin-left: ${Sizes.smallMargin};
        margin-right: ${Sizes.smallMargin};
    }


`;

const MeetingsContainer = styled.div`
    margin-top: ${Sizes.defaultPadding};
    max-height: 60vh;
    overflow: auto;
`;

const SecondaryButton = styled(Button).attrs({
    className: 'existing-meetings-dialog__secondary-button',
})`
    --mdc-theme-primary: ${p => p.theme.colors.modalPrimaryColor};

    height: auto;
    padding-top: ${Sizes.smallMargin};
    padding-bottom: ${Sizes.smallMargin};
    border-radius: ${Sizes.defaultBorderRadius};
    border: 1px solid var(--mdc-theme-primary);
    font-family: inherit;
    font-weight: 400;
`;

const NoOutlineButton = styled(Button).attrs({
    className: 'existing-meetings-dialog__secondary-button',
})`
    --mdc-theme-primary: ${p => p.theme.colors.modalPrimaryColor};
    text-decoration: underline;
    height: auto;
    border: none;
    font-family: inherit;
    font-size: 80%;
    font-weight: 400;
`;


const ExistingSection = styled.div.attrs({
    className: 'existing-meetings-dialog__existing-meeting-section',
})`
    color: ${p => p.theme.colors.primaryTextInverted};
    padding-left: ${Sizes.defaultPadding};
    padding-right: ${Sizes.defaultPadding};
`;

const ExistingRow = styled.div.attrs({
    className: 'existing-meetings-dialog__existing-meeting-row',
})`
    display: flex;
    justify-content: space-between;
    line-height: 2;
`;

const ExistingRowSide = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
`

const TimeSpan = styled.div`
    min-width: 3em;
`;

const DeleteMessage = styled.span`
    font-size: 80%;
`

const ExistingHeader = styled.div`
`

const SetNewMeeting = styled.div.attrs({
    className: 'existing-meetings-dialog__set-new-meeting-button',
})`
    box-shadow: 0 -2px 10px -5px #000;
    height: ${EXTRA_BOTTOM_SPACE_FOR_ALLOW_ADDITIONAL};
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    align-items: center;
    padding: ${Sizes.defaultPadding};
`;

const SetAnotherMeetingButton = styled(Button).attrs({
    className: 'existing-meetings-dialog__set-another-meeting-button',
})`
    --mdc-theme-primary: ${p => p.theme.colors.modalPrimaryColorInverted};

    height: auto;
    padding-top: ${Sizes.mediumPadding};
    padding-bottom: ${Sizes.mediumPadding};
    background-color: ${p => p.theme.colors.modalPrimaryColor} !important;
    border-radius: ${Sizes.defaultBorderRadius};
    font-weight: 400;
    width: 100%;
`;


class ExistingMeetingPopOver extends PureComponent {
    state = {isLoading: false};

    showToast(message) {
        this.toast.getWrappedInstance().show(message);
    }

    setReplaceMeetingMode = (group) => {
        this.props.setExistingMeetingsGroupMode('replace', group);
        this.showToast(this.props.intl.formatMessage({id: 'schedule.meetingWillReplaceExisting'}));
        this.setState({hidden: true});
    };

    setAdditionalMeetingMode = () => {
        this.props.setExistingMeetingsGroupMode('additional');
        this.showToast(this.props.intl.formatMessage({id: 'schedule.meetingWillBeScheduledInAddition'}));
        this.setState({hidden: true})
    };

    setUpdateMeetingsMode = (group) => {
        this.props.setExistingMeetingsGroupMode('modify', group);
        this.setState({hidden: true})
    };

    removeMeetings = (meetings, showMultipleMessage) => {
        let title, body, successMessageId;
        if (meetings.length > 1 && !showMultipleMessage) {
            title = (<FormattedMessage id="schedule.removeMeetingsTitle"/>);
            body = (<FormattedMessage id="schedule.removeMeetingsBody"/>);
            successMessageId = 'schedule.meetingsRemoveScheduleAnother'
        } else {
            title = (<FormattedMessage id="schedule.removeMeetingTitle"/>);
            body = (<FormattedMessage id="schedule.removeMeetingBody"/>);
            successMessageId = 'schedule.meetingRemoveScheduleAnother'
        }

        const {FullName, FirstName} = this.props.config.Customer;

        this.dialog.show(title, body, (accept) => {
            if (accept) {
                this.setState({isLoading: true})
                this.props.removeMeetings(meetings.map((meeting) => this.getMeetingMeta(meeting, FullName || FirstName)), (err) => {
                    this.setState({isLoading: false})

                    if (err) {
                        return this.showToast(this.props.intl.formatMessage({id: err.message}));
                    }


                    this.setState({hidden: true});
                    this.showToast(this.props.intl.formatMessage({id: successMessageId}));
                });
            }
        });
    };

    getMeetingMeta = ({MeetingId, StartTime, ServicesNames}, fullName) => {
        return {
            meetingId: MeetingId,
            startTime: StartTime,
            servicesNames: ServicesNames,
            fullName
        }
    };

    getExistingSection = (groupedSection) => {
        const {allowUpdates} = groupedSection;
        const containOfMoreThanOneMeeting = groupedSection.meetingsLabel.length > 1;
        const groupHasMultipleAppointments = groupedSection.meetings.length > 1;

        return <ExistingSection key={groupedSection.date}>
            <ExistingHeader>{humanizeDayOfWeek(groupedSection.date, true)}</ExistingHeader>
            {groupedSection.meetingsLabel.map(({
                                                   startTimeLabel,
                                                   servicesLabel,
                                                   meeting,
                                                   enableDeletion,
                                                   combinedMeetings
                                               }) => {
                    return (<ExistingRow key={startTimeLabel + servicesLabel}>
                        <ExistingRowSide>
                            <TimeSpan>{startTimeLabel}</TimeSpan>
                            <div>{servicesLabel}</div>
                        </ExistingRowSide>
                        {(enableDeletion && groupedSection.meetingsLabel.length > 1) && <div>
                            <NoOutlineButton
                                onClick={() => this.removeMeetings(combinedMeetings || [meeting], !!combinedMeetings)}
                            ><FormattedMessage id="cancelMeeting"/></NoOutlineButton>
                        </div>}
                    </ExistingRow>)
                }
            )}

            <div>
                <DialogActions>
                    {allowUpdates &&
                        <SecondaryButton
                            onClick={() => this.removeMeetings(groupedSection.meetings, groupHasMultipleAppointments)}
                        ><FormattedMessage
                            id={containOfMoreThanOneMeeting ? 'scheduleDeleteAll' : 'cancelMeeting'}/></SecondaryButton>}
                    {allowUpdates &&
                        <SecondaryButton onClick={() => this.setReplaceMeetingMode(groupedSection)}><FormattedMessage
                            id={containOfMoreThanOneMeeting ? 'scheduleAndReplaceAll' : 'schedule.scheduleAndReplace'}/></SecondaryButton>}
                    {allowUpdates && groupedSection.canReschedule &&
                        <ModalPrimary onClick={() => this.setUpdateMeetingsMode(groupedSection)}><FormattedMessage
                            id="schedule.updateStartTime"/></ModalPrimary>}

                    {!allowUpdates &&
                        <ModalPrimary><Link
                            href={`tel:${this.props.config && this.props.config.BusinessPhoneNumber}`}
                            custom-theme="shallow-button"
                            icon="favorite"><FormattedMessage
                            id="schedule.callUs"/></Link></ModalPrimary>}

                </DialogActions>
                {allowUpdates &&
                    <DeleteMessage><FormattedMessage id="schedule.deleteAndReplacingMessage"/></DeleteMessage>}
            </div>
            <Separator/>
        </ExistingSection>
    }

    getSetAnotherMeetingSection = (labelId, onClick) => {
        return <SetNewMeeting>
            <SetAnotherMeetingButton onClick={onClick}>
                <FormattedMessage id={labelId}/>
            </SetAnotherMeetingButton>
        </SetNewMeeting>
    }

    setIgnoreWaitingList = () => {
        this.setState({hidden: true})
        this.props.setIgnoreWaitingList();
    }

    getWaitingListSection() {
        if (!this.props.waitingListItem) {
            return (<div></div>);
        }

        return (<div>
            <H2 invertedColors={true} semiBold={true} center={true}>
                <FormattedMessage id="you_are_registered_to_waiting_list"/>
            </H2>
            <VerticalSpace/>
            {[this.props.waitingListItem].map(this.getWaitingListItemDetails.bind(this))}
            {!this.props.hasActualExtraMeetings && this.getSetAnotherMeetingSection('schedule_a_meeting', () => this.setIgnoreWaitingList())}


        </div>);
    }

    deleteWaitingListItem(waitingListItem) {
        const title = this.props.intl.formatMessage({id: 'remove_waiting_list_item'})
        const body = this.props.intl.formatMessage({id: 'remove_waiting_list_item_description'})

        this.dialog.show(title, body, (accept) => {
            if (accept) {
                this.setState({isLoading: true})

                this.props.removeWaitingListItem(waitingListItem._id, (err) => {

                    this.setState({isLoading: false})

                    if (err) {
                        return this.showToast(this.props.intl.formatMessage({id: extractError(err)}));
                    }

                    if (!isNonEmptyArray(this.props.existingMeetings)) {
                        this.setState({hidden: true});
                    }

                    this.showToast(this.props.intl.formatMessage({id: 'successfully_removed_waiting_list_item'}));
                });
            }
        });
    }

    getWaitingListItemDetails(waitingListItem) {
        const services = waitingListItem.services.map((x) => x.label).join(', ');
        return <ExistingSection key={waitingListItem._id}>
            <ExistingRow>
                <ExistingRowSide>
                    {services}
                </ExistingRowSide>
                <div>
                    <NoOutlineButton
                        onClick={() => this.deleteWaitingListItem(waitingListItem)}
                    ><FormattedMessage id="app.cancel"/></NoOutlineButton>
                </div>
            </ExistingRow>
        </ExistingSection>
    }

    getExistingMeetingsSection() {
        if (!this.hasActualExtraMeetings) {
            return (<div></div>);
        }

        return <div>
            <H2 invertedColors={true} semiBold={true} center={true} noPadding={!!this.props.waitingListItem}>
                {this.props.existingMeetings.length === 1 ? <FormattedMessage id="schedule.existingMeetingHeader"/> :
                    <FormattedMessage id="schedule.foundExistingMeetings"/>}
            </H2>
            <MeetingsContainer>
                {this.props.existingMeetings.map(this.getExistingSection)}
            </MeetingsContainer>
            {this.props.allowAdditionalMeetings && this.getSetAnotherMeetingSection('schedule.scheduleInAddition', this.setAdditionalMeetingMode.bind(this))}
        </div>
    }

    get hasActualExtraMeetings() {
        return isNonEmptyArray(this.props.existingMeetings);
    }

    render() {
        const hidden = this.props.hidden || this.state.hidden;
        const ltr = !this.props.intl.locale.includes('he');

        const spacer = (this.props.waitingListItem && this.hasActualExtraMeetings) ?
            (<PlainContainer><VerticalSpace/><Separator/></PlainContainer>) :
            <VerticalSpace/>;

        return (<Wrapper className="my-wrapper">
            <CSSTransition
                timeout={300}
                in={!hidden}
                unmountOnExit={true}
                classNames="mask">
                <Mask></Mask>
            </CSSTransition>
            <CSSTransition
                timeout={500}
                in={!hidden}
                unmountOnExit={true}
                classNames="alert">
                <Message extraBottomPadding={this.props.allowAdditionalMeetings} ltr={ltr}>
                    {this.getWaitingListSection()}
                    {spacer}
                    {this.getExistingMeetingsSection()}
                    <CSSTransition timeout={300} in={this.state.isLoading} unmountOnExit
                                   classNames="mask">
                        <div>
                            <Mask/>
                            <LoaderWrapper loading={this.state.isLoading}>
                                <LinearProgress determinate={false}/>
                            </LoaderWrapper>
                        </div>
                    </CSSTransition>
                </Message>
            </CSSTransition>
            <Confirm ref={dialog => this.dialog = dialog} ltr={ltr}/>
            <Toast ref={(toast) => this.toast = toast}/>
        </Wrapper>);
    }
}

export default injectIntl(ExistingMeetingPopOver, {withRef: true});
